import type { MapMarker, MapMarkerArcStyle, MarkerArcLayerOptions } from '@gpx-spa/geo-domain'
import { resolveMarkerClusterColor } from './marker-cluster-color.js'

export const MARKER_ARC_DEFAULTS = {
  minSegmentMeters: 50,
  lineColor: '#333333',
  lineWidth: 2,
  lineOpacity: 0.85,
  lineGeodesicSteps: 50,
} as const

export type ArcLatLng = {
  lat: number
  lng: number
  distanceTo(other: ArcLatLng): number
}

export type ArcSegmentPlan = {
  from: ArcLatLng
  to: ArcLatLng
  payload: Record<string, unknown>
  style: Required<
    Pick<MapMarkerArcStyle, 'lineColor' | 'lineWidth' | 'lineOpacity' | 'lineGeodesicSteps'>
  >
}

/** Groups markers with {@link MapMarker.arc} and sorts by {@link MapMarkerArc.order}. */
export function groupMarkersByArcGroup(markers: MapMarker[]): Map<string, MapMarker[]> {
  const groups = new Map<string, MapMarker[]>()
  for (const marker of markers) {
    const arc = marker.arc
    if (!arc) continue
    const groupId = arc.groupId.trim()
    if (!groupId) continue
    if (!Number.isFinite(arc.order)) continue
    const list = groups.get(groupId) ?? []
    list.push(marker)
    groups.set(groupId, list)
  }
  for (const list of groups.values()) {
    list.sort((a, b) => (a.arc?.order ?? 0) - (b.arc?.order ?? 0))
  }
  return groups
}

/** Line color: arc.style → marker clusterColor / pin / circle fill → default. */
export function resolveMarkerArcLineColor(marker: MapMarker): string {
  const fromArc = marker.arc?.style?.lineColor?.trim()
  if (fromArc) return fromArc
  return resolveMarkerClusterColor(marker) ?? MARKER_ARC_DEFAULTS.lineColor
}

export function mergedArcStyle(fromMarker: MapMarker): ArcSegmentPlan['style'] {
  const style = fromMarker.arc?.style ?? {}
  return {
    lineColor: resolveMarkerArcLineColor(fromMarker),
    lineWidth: style.lineWidth ?? MARKER_ARC_DEFAULTS.lineWidth,
    lineOpacity: style.lineOpacity ?? MARKER_ARC_DEFAULTS.lineOpacity,
    lineGeodesicSteps: style.lineGeodesicSteps ?? MARKER_ARC_DEFAULTS.lineGeodesicSteps,
  }
}

function arcPayloadForGroup(markers: MapMarker[]): Record<string, unknown> {
  const sorted = [...markers].sort((a, b) => (a.arc?.order ?? 0) - (b.arc?.order ?? 0))
  for (const marker of sorted) {
    const payload = marker.arc?.payload
    if (payload && typeof payload === 'object') {
      return { ...payload }
    }
  }
  return {}
}

function isArcEnabled(markers: MapMarker[], arcOptions?: MarkerArcLayerOptions): boolean {
  if (arcOptions?.enabled === false) return false
  return markers.some((m) => m.arc?.groupId?.trim())
}

/** Pure segment planning — cluster/layout resolution stays in the provider. */
export function planArcSegments(
  markers: MapMarker[],
  resolveLatLng: (marker: MapMarker) => ArcLatLng,
  arcOptions?: MarkerArcLayerOptions,
): ArcSegmentPlan[] {
  if (!isArcEnabled(markers, arcOptions)) return []
  const minSegmentMeters = arcOptions?.minSegmentMeters ?? MARKER_ARC_DEFAULTS.minSegmentMeters
  const segments: ArcSegmentPlan[] = []
  for (const group of groupMarkersByArcGroup(markers).values()) {
    if (group.length < 2) continue
    const payload = arcPayloadForGroup(group)
    for (let i = 0; i < group.length - 1; i++) {
      const fromMarker = group[i]!
      const toMarker = group[i + 1]!
      const from = resolveLatLng(fromMarker)
      const to = resolveLatLng(toMarker)
      if (from.distanceTo(to) < minSegmentMeters) continue
      segments.push({
        from,
        to,
        payload,
        style: mergedArcStyle(fromMarker),
      })
    }
  }
  return segments
}
